import type { TableRow } from "./TableRow";
import type { DataTableColumn } from "./types";

export const DATA_TABLE_INDEX_COLUMN_WIDTH = "48px";
export const DATA_TABLE_ACTIONS_COLUMN_WIDTH = "minmax(120px, auto)";
export const DATA_TABLE_DEFAULT_COLUMN_WIDTH = "minmax(0, 1fr)";
export const DATA_TABLE_DEFAULT_MIN_WIDTH = "760px";
// 본문 행 높이 하한의 기본값. auto는 종전 동작 — 그 표에서 가장 높은 셀이 행 높이를 정한다.
export const DATA_TABLE_DEFAULT_ROW_MIN_HEIGHT = "auto";
// 셀 세로 패딩. 기본은 텍스트 한 줄 기준, tight는 자체 높이를 가진 콘텐츠(섬네일 등)용으로
// 여백만 남긴다. 자유 길이가 아니라 두 값만 두어 0(구분선에 맞닿음)이 표현되지 않게 한다.
export const DATA_TABLE_CELL_PADDING_BLOCK = "4px";
export const DATA_TABLE_CELL_PADDING_BLOCK_TIGHT = "2px";
// caption header는 본문보다 한 단계 넉넉한 상하 리듬을 쓴다. checkbox/reorder chrome도 같은
// header row 안에서 이 값 이상을 확보해 배경을 중립화해도 header boundary가 눌려 보이지 않는다.
export const DATA_TABLE_HEADER_CELL_PADDING_BLOCK = "6px";
// 재정렬 핸들(우측 끝 햄버거) 전용 고정 트랙.
export const DATA_TABLE_REORDER_HANDLE_COLUMN_WIDTH = "44px";
// 선택 체크박스(좌측 끝, index 앞) 전용 고정 트랙. 1.25rem checkbox를 가운데 놓고
// 인접 데이터 셀의 8px padding과 합쳐 콘텐츠까지 약 15px 리듬을 만든다.
export const DATA_TABLE_SELECT_COLUMN_WIDTH = "36px";
// 선택 체크박스 크기. 기본(1.7em)보다 작게 둬 셀·행 경계와 겹치지 않게 한다(표·그리드 공용).
export const SELECT_CHECKBOX_SIZE = "1.25rem";

type DataTableTrackOptions = {
  showSelection?: boolean;
  showIndex: boolean;
  showActions: boolean;
  actionsColumnWidth?: string;
  showReorderHandle?: boolean;
};

export function buildDataTableGridTemplateColumns<Row extends TableRow>(
  columns: readonly DataTableColumn<Row>[],
  options: DataTableTrackOptions,
): string {
  const templateColumns = columns.map((column) => column.width ?? DATA_TABLE_DEFAULT_COLUMN_WIDTH);

  if (options.showIndex) {
    templateColumns.unshift(DATA_TABLE_INDEX_COLUMN_WIDTH);
  }

  // 선택 트랙은 index 앞(가장 왼쪽).
  if (options.showSelection) {
    templateColumns.unshift(DATA_TABLE_SELECT_COLUMN_WIDTH);
  }

  if (options.showActions) {
    templateColumns.push(options.actionsColumnWidth ?? DATA_TABLE_ACTIONS_COLUMN_WIDTH);
  }

  // 재정렬 핸들은 항상 가장 오른쪽 트랙.
  if (options.showReorderHandle) {
    templateColumns.push(DATA_TABLE_REORDER_HANDLE_COLUMN_WIDTH);
  }

  return templateColumns.join(" ");
}

export function buildDataTableGridMinWidth<Row extends TableRow>(
  columns: readonly DataTableColumn<Row>[],
  options: DataTableTrackOptions,
): string {
  const minWidths = columns.map((column) =>
    resolveTrackMinWidth(column.width ?? DATA_TABLE_DEFAULT_COLUMN_WIDTH),
  );

  if (options.showIndex) {
    minWidths.unshift(resolveTrackMinWidth(DATA_TABLE_INDEX_COLUMN_WIDTH));
  }

  if (options.showSelection) {
    minWidths.unshift(resolveTrackMinWidth(DATA_TABLE_SELECT_COLUMN_WIDTH));
  }

  if (options.showActions) {
    minWidths.push(
      resolveTrackMinWidth(options.actionsColumnWidth ?? DATA_TABLE_ACTIONS_COLUMN_WIDTH),
    );
  }

  if (options.showReorderHandle) {
    minWidths.push(resolveTrackMinWidth(DATA_TABLE_REORDER_HANDLE_COLUMN_WIDTH));
  }

  return `calc(${minWidths.join(" + ")})`;
}

function resolveTrackMinWidth(width: string): string {
  const trimmedWidth = width.trim();
  const minmaxMatch = /^minmax\(([^,]+),/.exec(trimmedWidth);
  if (minmaxMatch) {
    return resolveTrackMinWidth(minmaxMatch[1].trim());
  }

  if (trimmedWidth === "0" || trimmedWidth.endsWith("fr") || trimmedWidth === "auto") {
    return "0px";
  }

  return trimmedWidth;
}
